import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Modal,
    Form,
    Image
} from 'react-bootstrap';
import axios from 'axios';
import {
    faClock,
    faEdit
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useLanguage } from '../contexts/LanguageContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import { useAuth } from '../contexts/AuthContext';
import { ucfirst } from '../utils/utils';
import {
    setFlashMessage,
    useWebsite
} from '../contexts/WebsiteContext';
import config from '../config/config';

const Opening = () => {
    const { t } = useTranslation();
    const { activeLanguage } = useLanguage();
    const { restoData, setRestoData } = useResto();
    const { isModeAdmin } = useMode();
    const { token } = useAuth();
    const { setIsLoading } = useWebsite();

    const availableLanguages =
        restoData?.availableLanguages || [];

    const { apiUrl } = config;
    const apiPath = `${apiUrl}resto`;

    const [editModalOpen, setEditModalOpen] =
        useState(false);

    const [editedOpenings, setEditedOpenings] =
        useState({});

    /* ================= INIT DATA ================= */

    useEffect(() => {
        if (!restoData || !availableLanguages.length)
            return;

        const openings = {};

        availableLanguages.forEach(language => {
            const key =
                `opening${ucfirst(language)}`;

            openings[key] =
                restoData[key] || '';
        });

        setEditedOpenings(openings);

    }, [restoData, availableLanguages]);

    /* ================= HANDLERS ================= */

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        setEditedOpenings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        if (!restoData?.website) return;

        setIsLoading(true);

        try {
            const response = await axios.put(
                `${apiPath}/${restoData.website}`,
                editedOpenings,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);

            setFlashMessage(
                t('SUCCESS_UPDATED_MESSAGE'),
                'success'
            );

            setEditModalOpen(false);

        } catch {
            setFlashMessage(
                t('ERROR_MESSAGE'),
                'error'
            );
        }

        setIsLoading(false);
    };

    /* ================= RENDER ================= */

    const currentOpeningKey =
        `opening${ucfirst(activeLanguage)}`;

    return (
        <div>

            <h2>
                <FontAwesomeIcon
                    icon={faClock}
                />{' '}
                {t('OPENING')}

                {isModeAdmin && (
                    <Button
                        variant="primary"
                        size="sm"
                        style={{ marginLeft: 10 }}
                        onClick={() =>
                            setEditModalOpen(true)
                        }
                    >
                        <FontAwesomeIcon
                            icon={faEdit}
                        />
                    </Button>
                )}
            </h2>

            {/* ================= USER VIEW ================= */}

            {!isModeAdmin && (
                <p>
                    {editedOpenings[
                        currentOpeningKey
                    ] || restoData?.[currentOpeningKey]}
                </p>
            )}

            {/* ================= ADMIN VIEW ================= */}

            {isModeAdmin && (
                <>
                    {availableLanguages.map(lang => {
                        const key =
                            `opening${ucfirst(lang)}`;

                        return (
                            <div key={lang}>
                                <p>
                                    <Image
                                        src={`${config.kubuniImagesUrl}general-icons/flag_${lang}_icon_24.png`}
                                        className="flag-label"
                                        alt={lang}
                                    />{' '}
                                    {editedOpenings[key]}
                                </p>
                            </div>
                        );
                    })}
                </>
            )}

            {/* ================= MODAL ================= */}

            <Modal
                show={editModalOpen}
                onHide={() =>
                    setEditModalOpen(false)
                }
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('EDIT')}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        {availableLanguages.map(
                            lang => {
                                const key =
                                    `opening${ucfirst(lang)}`;

                                return (
                                    <Form.Group
                                        key={lang}
                                        controlId={key}
                                    >
                                        <Form.Label>
                                            <Image
                                                src={`${config.kubuniImagesUrl}general-icons/flag_${lang}_icon_24.png`}
                                                className="flag-label"
                                                alt={lang}
                                            />
                                        </Form.Label>

                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name={key}
                                            value={
                                                editedOpenings[key] ||
                                                ''
                                            }
                                            onChange={
                                                handleEditInputChange
                                            }
                                        />
                                    </Form.Group>
                                );
                            }
                        )}
                    </Form>
                </Modal.Body>

                <Modal.Footer className="justify-content-center">
                    <Button
                        variant="secondary"
                        onClick={() =>
                            setEditModalOpen(false)
                        }
                    >
                        {t('CANCEL')}
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                    >
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default Opening;